/**
 * THE SCAN FOLDER, seen from inside the app.
 *
 * ⭐ WHAT THIS IS. A web page cannot open `\\srv-fs01\scans` on its own — there
 * is no API for "read this UNC path", and there never will be, because a page
 * that could read arbitrary paths could read anything on the machine. What it
 * CAN do is have the scheduler HAND it that folder once, through the browser's
 * own directory picker, and then list it. That is the File System Access API.
 *
 * ⛔ THERE ARE NO CREDENTIALS ANYWHERE IN THIS FILE, and there is nowhere to put
 * any. The folder is opened by the browser using the signed-in Windows session,
 * so each person reaches exactly the shares they are already entitled to reach
 * and the application never sees a password. (The scanner's own account for
 * WRITING into the folder lives on the scanner, set by IT.)
 *
 * ⚠️ IT READS, AND IT ADDS. It NEVER deletes or overwrites. "Already filed" is
 * tracked in our own database, fingerprinted on name + size + modified time,
 * rather than by moving or deleting the original.
 *
 * ---- ⛔⛔ "A PAGE CANNOT WRITE TO SOMEBODY'S DISK" WAS WRONG (2026-09-10) ---
 *
 * > Pete: *"Still not letting me drag from sharepoint to local."*
 *
 * ⛔ THAT REFUSAL WAS BUILT ON A CLAIM THIS FILE ITSELF DISPROVES. The scan
 * folder was opened `mode: 'read'`, and "the handle we hold is read-only" was
 * then stated as "the browser forbids writing" — a §4.14 substitution of the
 * narrow thing that was checked for the broad thing that was said. ⭐ The File
 * System Access API writes perfectly well: `showDirectoryPicker({ mode:
 * 'readwrite' })`, `getFileHandle(name, { create: true })`, `createWritable()`.
 * The scheduler grants it once, per folder, in the browser's own prompt.
 *
 * ⛔ SO THE RULE IS NARROWED, NOT DROPPED: this module may CREATE a file that
 * does not exist and may never touch one that does. `writeNewFile` refuses an
 * existing name and picks ` (2)` instead, so a returning document can never
 * land on top of a scan that has not been filed yet.
 *
 * ⚠️ AND WRITE PERMISSION IS ASKED FOR SEPARATELY from read. A site that only
 * ever reads scans is never prompted for more than it uses — `ensureWrite
 * Permission` is called on the drop, not on the folder read.
 *
 * Support: Chrome/Edge 86+. Firefox, Safari and every mobile browser implement
 * only the sandboxed half and cannot do this at all — `folderPickerSupported()`
 * exists so the UI offers a plain file input there instead of a dead button.
 */

// ---------------------------------------------------------------------------
// Minimal typings
// ---------------------------------------------------------------------------
// ⚠️ DECLARED LOCALLY rather than relying on the DOM lib. These types moved
// between TypeScript releases, and a build that fails on a lib version is a
// worse problem than four interfaces nobody has to maintain.

type PermissionState = 'granted' | 'denied' | 'prompt';

interface FsHandlePermissionDescriptor { mode?: 'read' | 'readwrite' }

interface FsWritable {
    write(data: Blob): Promise<void>;
    close(): Promise<void>;
}

interface FsFileHandle {
    kind: 'file';
    name: string;
    getFile(): Promise<File>;
    /** ⚠️ Absent on a read-only handle and on browsers without the write half. */
    createWritable?(): Promise<FsWritable>;
}

interface FsDirectoryHandle {
    kind: 'directory';
    name: string;
    values(): AsyncIterableIterator<FsFileHandle | FsDirectoryHandle>;
    queryPermission?(d?: FsHandlePermissionDescriptor): Promise<PermissionState>;
    requestPermission?(d?: FsHandlePermissionDescriptor): Promise<PermissionState>;
    /**
     * ⛔ `create: false` IS HOW WE ASK "does this name already exist" — it
     * REJECTS with NotFoundError rather than returning null, which is why
     * `nameIsFree` is a try/catch and not an `if`.
     */
    getFileHandle?(name: string, opts?: { create?: boolean }): Promise<FsFileHandle>;
    /**
     * ⛔⛔ THIS IS A REAL DELETE. There is no Recycle Bin behind the File System
     * Access API — `removeEntry` unlinks the file and nothing in the browser, the
     * OS shell or this app can bring it back. ⚠️ Compare `deleteScan` in
     * `api/client.ts`, whose SharePoint delete is recoverable for 93 days. These
     * two are not the same act and must not be described to the user as if they
     * were.
     */
    removeEntry?(name: string, opts?: { recursive?: boolean }): Promise<void>;
}

interface PickerWindow {
    showDirectoryPicker?: (opts?: { mode?: 'read' | 'readwrite'; id?: string }) => Promise<FsDirectoryHandle>;
}

export type ScanFolderHandle = FsDirectoryHandle;

/** Chrome/Edge only. Everything else gets the plain file input. */
export function folderPickerSupported(): boolean {
    return typeof window !== 'undefined'
        && typeof (window as unknown as PickerWindow).showDirectoryPicker === 'function';
}

// ---------------------------------------------------------------------------
// Remembering the folder
// ---------------------------------------------------------------------------
// ⭐ The handle is STRUCTURED-CLONEABLE, so IndexedDB can hold it across reloads
// and browser restarts. localStorage cannot — it only stores strings, and a
// handle stringifies to "[object FileSystemDirectoryHandle]", which is how this
// gets built wrong.

const DB_NAME = 'symbia-dock';
const STORE = 'handles';
const KEY = 'scanFolder';

function withStore<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest): Promise<T | null> {
    return new Promise((resolve) => {
        let req: IDBOpenDBRequest;
        try {
            req = indexedDB.open(DB_NAME, 1);
        } catch {
            resolve(null);
            return;
        }
        req.onupgradeneeded = () => {
            if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE);
        };
        req.onerror = () => resolve(null);
        req.onsuccess = () => {
            const db = req.result;
            try {
                const tx = db.transaction(STORE, mode);
                const out = fn(tx.objectStore(STORE));
                out.onsuccess = () => resolve((out.result ?? null) as T | null);
                out.onerror = () => resolve(null);
                tx.oncomplete = () => db.close();
            } catch {
                db.close();
                resolve(null);
            }
        };
    });
}

export function rememberFolder(handle: ScanFolderHandle): Promise<unknown> {
    return withStore('readwrite', s => s.put(handle, KEY));
}

export function recallFolder(): Promise<ScanFolderHandle | null> {
    return withStore<ScanFolderHandle>('readonly', s => s.get(KEY));
}

export function forgetFolder(): Promise<unknown> {
    return withStore('readwrite', s => s.delete(KEY));
}

// ---------------------------------------------------------------------------
// Picking and permission
// ---------------------------------------------------------------------------

/**
 * Ask for the folder. MUST be called straight from a click.
 *
 * ⚠️ The picker refuses to open from a timer or a page-load handler, by design —
 * so this cannot be called on mount to "restore" the folder. Restoring uses
 * `recallFolder` plus `ensureReadPermission` instead.
 *
 * Returns null when the user cancels, which is not an error.
 */
export async function pickScanFolder(): Promise<ScanFolderHandle | null> {
    const w = window as unknown as PickerWindow;
    if (!w.showDirectoryPicker) return null;
    try {
        /**
         * ⭐⭐ `readwrite`, SINCE 2026-09-10 — Pete drags documents back OUT of
         * SharePoint into this folder, and a read handle cannot take them.
         *
         * ⛔ ~~"Asking for readwrite would prompt harder and buy nothing."~~ It
         * buys the drop. ⚠️ It DOES prompt harder: Chrome's picker shows "Edit
         * files" rather than "View files", which is a fair description of what
         * the app now does and is asked for once per folder.
         *
         * ⚠️ A FOLDER REMEMBERED BEFORE TODAY IS STILL READ-ONLY — IndexedDB
         * holds the old handle and this line never runs for it. That is why the
         * write path calls `ensureWritePermission` and upgrades in place rather
         * than assuming this mode.
         *
         * `id` makes the browser reopen at the same place next time.
         */
        const handle = await w.showDirectoryPicker({ mode: 'readwrite', id: 'symbia-scan-folder' });
        await rememberFolder(handle);
        return handle;
    } catch {
        // AbortError when the user cancels. Nothing to report.
        return null;
    }
}

/**
 * Do we still have read access to a remembered folder?
 *
 * ⚠️ `interactive` MUST BE FALSE unless this is called from a click. Chrome 122+
 * offers "Allow on every visit", so a remembered folder usually answers
 * 'granted' with no prompt at all — but when it does not, requesting outside a
 * user gesture throws, and the folder silently appears empty rather than
 * appearing locked.
 */
export async function ensureReadPermission(
    handle: ScanFolderHandle, interactive = false,
): Promise<PermissionState> {
    const opts: FsHandlePermissionDescriptor = { mode: 'read' };
    try {
        const current = handle.queryPermission ? await handle.queryPermission(opts) : 'granted';
        if (current === 'granted') return 'granted';
        if (!interactive || !handle.requestPermission) return current;
        return await handle.requestPermission(opts);
    } catch {
        return 'denied';
    }
}

/**
 * ---- ⭐⭐ WRITE ACCESS, ASKED FOR SEPARATELY AND ONLY WHEN USED -------------
 *
 * ⚠️ `interactive` MUST BE FALSE unless the caller is inside a user gesture.
 * ⛔ AND A `drop` EVENT IS NOT ONE. The HTML spec's activation-triggering
 * events are click / mousedown / mouseup / pointerup / keydown / touchend —
 * `drop` is NOT on that list, so `requestPermission` from a drop handler may
 * reject with "must be handling a user gesture" in exactly the browser this
 * feature exists for.
 *
 * ⭐ SO IT IS TRIED ANYWAY AND THE FAILURE IS A SENTENCE, NOT A SILENCE. When
 * Chrome's "Allow on every visit" was ticked at pick time this returns
 * 'granted' with no prompt and the drop just works; when it does not, the
 * caller says which button to press. ⛔ What must never happen is the drop
 * doing nothing and looking broken — that is the whole complaint this fixes.
 */
export async function ensureWritePermission(
    handle: ScanFolderHandle, interactive = false,
): Promise<PermissionState> {
    const opts: FsHandlePermissionDescriptor = { mode: 'readwrite' };
    try {
        const current = handle.queryPermission ? await handle.queryPermission(opts) : 'granted';
        if (current === 'granted') return 'granted';
        if (!interactive || !handle.requestPermission) return current;
        return await handle.requestPermission(opts);
    } catch {
        return 'denied';
    }
}

/** ⭐ Can this browser, and this handle, write at all? Cheap and synchronous. */
export function folderWriteSupported(handle: ScanFolderHandle | null): boolean {
    return !!handle && typeof handle.getFileHandle === 'function';
}

/**
 * ---- ⛔⛔ REMOVE A SCAN THAT HAS BEEN TAKEN INTO DOCK (2026-09-10) ----------
 *
 * > Pete: *"sometimes it moves, sometimes it copies. Can we always make it move
 * > please?"*
 *
 * ⭐ THE PRECEDENT IS ALREADY IN THE PRODUCT AND THAT IS WHY THIS IS RIGHT:
 * `ingestScan` has always MOVED a SharePoint scan into `Filed/YYYY-MM` once Dock
 * has it, and reports `moved: false` when it could not. ⚠️ The local folder was
 * the one source that copied instead — so a scanning workstation's tray kept
 * growing with files that were already filed, greyed out, forever. THAT is the
 * inconsistency Pete is describing.
 *
 * ---- ⛔⛔ IT IS UNRECOVERABLE, AND THE CALLER MUST TREAT IT THAT WAY --------
 *
 * ⚠️ NO RECYCLE BIN. `removeEntry` unlinks the file. Windows' shell bin is not
 * involved because the browser is not the shell, so there is nothing to restore
 * from and no undo to offer.
 *
 * ⛔ SO IT IS NEVER CALLED BEFORE THE DESTINATION HAS CONFIRMED. Upload first,
 * read the document id back, and only then remove — see the call sites in
 * `Calendar`. A delete-then-upload ordering loses the only copy of a piece of
 * paper the first time the network blinks.
 *
 * ⛔ AND A FAILURE HERE IS NOT A FILING FAILURE. The document IS in Dock; the
 * original is merely still on the disk. Callers carry that as its own sentence
 * (`stuckInFolder`) rather than telling somebody their paperwork did not file.
 */
export async function deleteFile(handle: ScanFolderHandle, fileName: string): Promise<void> {
    if (!handle.removeEntry) throw new Error('This browser cannot remove a file from a folder.');
    await handle.removeEntry(fileName);
}

/**
 * ---- ⛔⛔ IT CREATES. IT NEVER OVERWRITES. ---------------------------------
 *
 * ⚠️ THE FOLDER IS SOMEBODY ELSE'S WORK QUEUE. A scan sitting in it that nobody
 * has filed yet is the ONLY copy of that piece of paper, and
 * `getFileHandle(name, { create: true })` on an existing name opens THAT file
 * and `createWritable()` truncates it. ⛔ A returning document that happened to
 * share a name would destroy an unfiled scan silently — there is no undo, no
 * Recycle Bin entry, and nobody would know which document was lost.
 *
 * ⭐ SO A TAKEN NAME IS SIDESTEPPED, NOT RESOLVED: `bol.pdf` becomes
 * `bol (2).pdf`, up to 50, and past that it refuses rather than guessing. Two
 * files in a tray is a five-second problem; one missing scan is a dispute.
 *
 * Returns the name actually written.
 */
export async function writeNewFile(
    handle: ScanFolderHandle, fileName: string, data: Blob,
): Promise<string> {
    if (!handle.getFileHandle) throw new Error('This browser cannot write to a folder.');
    const safe = sanitiseFileName(fileName);
    const name = await freeName(handle, safe);
    const fh = await handle.getFileHandle(name, { create: true });
    if (!fh.createWritable) throw new Error('The folder was opened read-only.');
    const w = await fh.createWritable();
    try {
        await w.write(data);
    } finally {
        /* ⛔ CLOSE IN A `finally`. An unclosed writable leaves a `.crswap`
           temp file beside the real one, which is what the scheduler would
           then find in the tray. */
        await w.close();
    }
    return name;
}

/**
 * ⛔ NOT COSMETIC. `getFileHandle` REJECTS on a name containing a path
 * separator — which is precisely what a templated document name like
 * `262/A00000310.pdf` would carry — and the rejection reads as "the drop did
 * nothing". Windows' other forbidden characters go the same way.
 */
export function sanitiseFileName(name: string): string {
    const cleaned = Array.from(name ?? '')
        /* ⛔ CONTROL CHARACTERS GO TOO, and they are filtered by CODE POINT
           rather than by a regex range. A literal \x00-\x1f class trips
           `no-control-regex` — and silencing that rule to keep the shorter
           spelling would be silencing a lint that is right about the general
           case for the sake of four characters. */
        .map(ch => (ch < ' ' || FORBIDDEN.has(ch) ? '-' : ch))
        .join('')
        .replace(/^\.+/, '')
        .trim();
    return cleaned.length > 0 ? cleaned.slice(0, 180) : 'document';
}

/** ⛔ Windows' reserved set, plus the separators `getFileHandle` itself rejects. */
const FORBIDDEN = new Set(['\\', '/', ':', '*', '?', '"', '<', '>', '|']);

/** `bol.pdf` → `bol (2).pdf`. Extension kept, because the OS opens by it. */
function suffixed(name: string, n: number): string {
    const dot = name.lastIndexOf('.');
    if (dot <= 0) return `${name} (${n})`;
    return `${name.slice(0, dot)} (${n})${name.slice(dot)}`;
}

async function nameIsFree(handle: ScanFolderHandle, name: string): Promise<boolean> {
    if (!handle.getFileHandle) return true;
    try {
        await handle.getFileHandle(name, { create: false });
        return false;
    } catch (err) {
        /* ⚠️ NotFoundError means free. Anything else — a permission failure, a
           disconnected share — must NOT be read as "free", or the next line
           creates a file we were told we could not create. */
        const kind = (err as { name?: string } | null)?.name;
        if (kind === 'NotFoundError') return true;
        if (kind === 'TypeMismatchError') return false;  // a DIRECTORY of that name
        throw err;
    }
}

async function freeName(handle: ScanFolderHandle, name: string): Promise<string> {
    if (await nameIsFree(handle, name)) return name;
    for (let n = 2; n <= 50; n++) {
        const candidate = suffixed(name, n);
        if (await nameIsFree(handle, candidate)) return candidate;
    }
    throw new Error(`The folder already holds 50 files called ${name}.`);
}

// ---------------------------------------------------------------------------
// Reading the folder
// ---------------------------------------------------------------------------

export interface ScanFile {
    name: string;
    size: number;
    lastModified: number;
    /**
     * ⭐ THE IDEMPOTENCY KEY, and what greys an already-filed scan out. Stable
     * across re-reads, distinct between two scans that happen to share a name,
     * and it survives the file being copied — which is what we want, because
     * the same bytes ARE the same document.
     */
    ref: string;
    handle: FsFileHandle;
}

/** name + size + modified. Kept short enough for NVARCHAR(200). */
export function scanRef(name: string, size: number, lastModified: number): string {
    return `${name.slice(0, 150)}|${size}|${lastModified}`;
}

export interface ListOptions {
    /** Lower-case, no dots. Anything else is ignored entirely. */
    allowedExtensions: string[];
    /** Stop after this many, newest first. Keeps a huge folder from hanging. */
    limit?: number;
}

/**
 * List the scans in the folder, newest first.
 *
 * ⚠️ THERE IS NO FILE-WATCHING API. The caller re-reads on a timer. That is
 * cheap for a folder holding tens of files and is not for one holding ten
 * thousand, which is why `limit` exists and why the scan folder should be a
 * tray rather than an archive.
 *
 * ⚠️ SUBFOLDERS ARE NOT WALKED, deliberately. An MFP writing into per-user
 * subfolders would otherwise make one scheduler's list include everyone's, and
 * the recursion has no natural bottom.
 */
export async function listScans(
    handle: ScanFolderHandle, opts: ListOptions,
): Promise<ScanFile[]> {
    const allowed = new Set(opts.allowedExtensions.map(e => e.replace(/^\./, '').toLowerCase()));
    const limit = opts.limit ?? 200;
    const out: ScanFile[] = [];

    // ⚠️ A HARD CEILING ON THE WALK ITSELF, not just on the result. Sorting by
    // date needs every entry, so a folder with fifty thousand files would stall
    // the panel before the sort ever ran.
    const MAX_ENTRIES = 2000;
    let seen = 0;

    for await (const entry of handle.values()) {
        if (++seen > MAX_ENTRIES) break;
        if (entry.kind !== 'file') continue;
        const dot = entry.name.lastIndexOf('.');
        const ext = dot > 0 ? entry.name.slice(dot + 1).toLowerCase() : '';
        if (!allowed.has(ext)) continue;
        try {
            const file = await (entry as FsFileHandle).getFile();
            out.push({
                name: entry.name,
                size: file.size,
                lastModified: file.lastModified,
                ref: scanRef(entry.name, file.size, file.lastModified),
                handle: entry as FsFileHandle,
            });
        } catch {
            // A file being written by the scanner right now cannot be opened.
            // Skipping it is correct — it will be there on the next read.
        }
    }

    out.sort((a, b) => b.lastModified - a.lastModified);
    return out.slice(0, limit);
}

/** Human-readable size, for a list where the number is a sanity check. */
export function prettyBytes(n: number): string {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
